import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Properties that turn an element into a containing block for position:fixed
 * descendants. If any ancestor of the drawer carries one, the drawer stops
 * being positioned against the viewport and is laid out against that ancestor
 * instead. On a scrolled page that puts it off screen while the scroll lock is
 * already engaged, leaving a frozen page with no visible menu.
 */
const CONTAINING_BLOCK_PROPERTIES = [
  'contain',
  'view-transition-name',
  'transform',
  'filter',
  'perspective',
  'backdrop-filter',
  'will-change',
  'content-visibility',
];

const NEUTRAL_VALUES = new Set(['none', 'normal', 'auto', 'visible', '']);

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Array<{ element: HTMLElement, cssText: string }>} */
  #neutralized = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#restoreAncestors();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Walk from the drawer up to the document root and strip every property that
   * would make an ancestor a containing block. Inline styles are used so they
   * beat any stylesheet rule whatever its specificity, and the previous inline
   * cssText is kept so it can be put back exactly on close.
   */
  #neutralizeAncestors() {
    this.#restoreAncestors();

    let element = this.refs.menuDrawer.parentElement;

    while (element instanceof HTMLElement) {
      const computed = getComputedStyle(element);
      const offending = CONTAINING_BLOCK_PROPERTIES.some((property) => {
        return !NEUTRAL_VALUES.has(computed.getPropertyValue(property).trim());
      });

      if (offending) {
        this.#neutralized.push({ element, cssText: element.style.cssText });

        for (const property of CONTAINING_BLOCK_PROPERTIES) {
          element.style.setProperty(property, 'none', 'important');
        }
      }

      element = element.parentElement;
    }
  }

  /**
   * Put back whatever inline styles the ancestors had before we touched them.
   */
  #restoreAncestors() {
    for (const { element, cssText } of this.#neutralized) {
      element.style.cssText = cssText;
    }

    this.#neutralized = [];
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    // Before the class lands, so the drawer is laid out against the viewport
    // from its very first frame.
    this.#neutralizeAncestors();

    // Set synchronously. This previously ran inside requestAnimationFrame, and
    // iOS suspends rAF callbacks while the page has scroll momentum - so on a
    // scrolled page the class never landed while the scroll lock already had.
    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });

    // Last-resort guard. If the drawer still is not on screen shortly after
    // opening, close it rather than leave the customer on a frozen page with no
    // menu and no way back.
    setTimeout(() => {
      if (!details.hasAttribute('open')) return;

      const rect = this.refs.menuDrawer.getBoundingClientRect();
      const onScreen = rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;

      if (!onScreen) this.#close(details);
    }, 400);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    // The scroll lock is only released when [open] is removed, which happens in
    // reset(). If the close animation never fires the page would stay locked
    // forever, so the reset is guaranteed by a timer too. Safe to run twice.
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;

      reset(details);
      this.#restoreAncestors();

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    onAnimationEnd(drawer || details, finish, { subtree: false });
    setTimeout(finish, 500);
  }

  /**
   * Attach animationend listeners so will-change is removed after animation,
   * clearing the stacking context so submenus position correctly.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
